import Tooltip from "@/ui/Tooltip"
import type { Placement } from "@floating-ui/react"
import type { RefObject } from "react"
import { useHudContext } from "../hud/shared/playbookHUDInfra"
import { METRIC_LABEL_BASELINE_TRIM_CLASS, useMetricHoverSwap } from "../hud/blocks/metrics/metricHoverSwap"
import { useMetricHoverTrigger } from "../hud/blocks/hoverSwapStickyZone"
import { useHoverSwapsDisabled } from "../hud/blocks/hoverSwapsDisabled"
import {
  MetricDefinitionPanel,
  useUnifiedHudPanels,
} from "../hud/blocks/panels/unifiedHoverPanels"
import { MetricTrigger } from "../hud/metric-details/MetricTrigger"
import { METRIC_DETAILS_HINT } from "../hud/metric-details/metricLabels"
import { TickerNumber } from "./TickerNumber"

const MEDIAN_INCOME_TOOLTIP = <div>
  <p className="mb-2 font-bold font-mono uppercase"> Median income</p>
  <p>Median income across the US working age population, in 2025 USD.</p>
  <p className="mt-2 border-t border-black/10 pt-1.5 font-sans text-[10px] text-[var(--race-background)]">{METRIC_DETAILS_HINT}</p>
</div>

// 2 significant digits, not fixed decimals: a forecast rendered as "$10.3M"
// implies far more precision than the model has, while values like "$1.5M"
// still keep their meaningful fractional digit.
const INCOME_FORMAT = {
  style: "currency" as const,
  currency: "USD",
  notation: "compact" as const,
  maximumSignificantDigits: 2,
}

export const MedianIncome = ({ oneLine = false, valueFirst = false, labelFontSize, label, tooltipPlacement = "top-start", tooltipPositionRef }: { oneLine?: boolean; valueFirst?: boolean; labelFontSize?: string; label?: string; tooltipPlacement?: Placement; tooltipPositionRef?: RefObject<HTMLElement | null> }) => {
  const ctx = useHudContext()
  const income = ctx.currentPhase?.hudMetrics?.medianUSIncome ?? 0
  const wrapperClassName = valueFirst
    ? "flex flex-col-reverse justify-end items-center w-min"
    : oneLine
      ? "flex w-full items-baseline gap-1.5 justify-between"
      : "flex items-start flex-col"

  const fontSize = oneLine ? "text-[10.5px]" : "text-[8px]"
  const numberFontSize = oneLine || valueFirst ? "text-[11px]" : "text-[10.5px]"
  // Inside a <MetricHoverSwap> provider the floating tooltip is suppressed:
  // hover is reported to the swap context instead, and a MetricHoverSwapTarget
  // elsewhere on the HUD renders this metric's definition in-canvas. The
  // MetricTrigger (click → details panel) stays either way.
  const metricSwap = useMetricHoverSwap()
  const hoverSwapsDisabled = useHoverSwapsDisabled()
  // Red-chip treatment (tab 3): the stat row stretches its items so the four
  // hover chips share one height — this trigger stretches its inner column to
  // match instead of centering it at natural height.
  const chipRow = metricSwap?.triggerHighlight === "red-chip"
  const unifiedPanels = useUnifiedHudPanels()
  // Mirrors the TickerNumber rendering below ("—" below $1K, compact USD).
  const metricHoverProps = useMetricHoverTrigger(
    "medianIncome",
    income < 1000 ? "—" : new Intl.NumberFormat("en-US", INCOME_FORMAT).format(income),
  )
  const trigger = (
    <MetricTrigger
      id="income"
      // self-stretch/px-1.5 belong to the production stats row; the px-1.5
      // is its tooltip hover surface. The swap row goes flush (the padding
      // would indent the figure off the policy column's edge), and hover-less
      // surfaces (the mobile dashboard) drop the padding too — no tooltips
      // there, and its narrow stat columns need the width.
      className={valueFirst && !metricSwap
        ? `flex items-start self-stretch ${hoverSwapsDisabled ? "" : "px-1.5"}`
        : chipRow ? "flex items-stretch gap-2" : "flex items-center gap-2"}
      {...metricHoverProps}
    >
      <div className={wrapperClassName}>
        <div className={`${fontSize} ${valueFirst ? "text-center" : "whitespace-nowrap"} ${valueFirst && metricSwap ? METRIC_LABEL_BASELINE_TRIM_CLASS : ""}`} style={labelFontSize ? { fontSize: labelFontSize } : undefined}>
          {label ?? "Median Income"}
        </div>
        <div className={`${numberFontSize} font-bold whitespace-nowrap ${valueFirst ? "" : ""}`}>
          {/* Below $1K the branch collapse has made the statistic
              meaningless — dash it rather than ticking to $100/$0. */}
          {income < 1000
            ? "—"
            : (
              <TickerNumber
                value={income}
                format={INCOME_FORMAT}
              />
            )}
        </div>
      </div>
    </MetricTrigger>
  )
  // Hover-less surfaces (the mobile dashboard): no definition tooltip — a
  // tap must go straight to the MetricTrigger's click (the details page)
  // instead of surfacing the hover affordance.
  if (metricSwap || hoverSwapsDisabled) return trigger
  return (
    <Tooltip
      placement={tooltipPlacement}
      positionRef={tooltipPositionRef}
      disableShadow={Boolean(unifiedPanels)}
      content={unifiedPanels ? (
        <MetricDefinitionPanel metricId="medianIncome" />
      ) : (
        <div className="p-2 w-[280px]">{MEDIAN_INCOME_TOOLTIP}</div>
      )}
    >
      {trigger}
    </Tooltip>
  )
}
